/**
 * ManualCheckDialog — record a manual checklist run for a Control Grid row.
 *
 * Variant A + Option 1 from the Phase 4 design discussion:
 *   - Per-check pass / fail / skip toggles (skip = "not run", does not count
 *     toward confidence; pass+fail are the only votes).
 *   - Free-text observation note + expiry date (defaults to today + 14d).
 *   - Live preview of derived status / confidence using the same threshold
 *     constants the automated builder uses (DEFAULT_THRESHOLDS).
 *   - Live preview of the full new manual-overrides.json with a single
 *     "Copy to clipboard" button. Operator pastes into
 *     internal/control-grid/manual-overrides.json and refreshes the page.
 *
 * Deliberately client-only: no backend write path. The JSON file remains the
 * single source of truth and stays git-reviewable.
 */

import { useEffect, useMemo, useState } from 'react';
import { Check, ClipboardCheck, Copy, Minus, X } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';

import {
  DEFAULT_THRESHOLDS,
  type CapabilityConfig,
  type ManualOverride,
  type OverridesFile,
  type RowStatus,
  type Thresholds,
} from '../utils/controlGrid/build';

type CheckState = 'pass' | 'fail' | 'skip';

interface ManualCheckDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  capability: CapabilityConfig;
  /** Current overrides file (used to merge the new override in). */
  currentOverridesFile: OverridesFile;
  /** Threshold overrides from capabilities.json, if any. */
  thresholds?: Partial<Thresholds>;
}

const DEFAULT_EXPIRY_DAYS = 14;

function isoDateInputDefault(daysFromNow: number): string {
  const d = new Date(Date.now() + daysFromNow * 24 * 60 * 60 * 1000);
  // <input type="date"> wants YYYY-MM-DD in local time.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Manual checklist semantics: only PASS and FAIL contribute to denominator.
 * SKIP means "not run today" and shouldn't tank confidence. If everything is
 * skipped we surface 'unknown' so the operator knows nothing was decided.
 */
function deriveManualStatus(
  states: CheckState[],
  thresholds: Thresholds,
): { status: RowStatus; confidence: number; passed: number; failed: number; skipped: number } {
  let passed = 0;
  let failed = 0;
  let skipped = 0;
  for (const s of states) {
    if (s === 'pass') passed += 1;
    else if (s === 'fail') failed += 1;
    else skipped += 1;
  }
  const denom = passed + failed;
  if (denom === 0) {
    return { status: 'unknown', confidence: 0, passed, failed, skipped };
  }
  const confidence = passed / denom;
  let status: RowStatus;
  if (failed === 0 && confidence >= thresholds.healthyMinConfidence) status = 'healthy';
  else if (failed === 0 && confidence >= thresholds.watchMinConfidence) status = 'watch';
  else if (failed > 0 && confidence >= thresholds.watchMinConfidence) status = 'watch';
  else status = 'broken';
  return { status, confidence, passed, failed, skipped };
}

function formatNote(
  passed: number,
  failed: number,
  skipped: number,
  freeText: string,
): string {
  const total = passed + failed + skipped;
  const ran = passed + failed;
  const ranPart = `Manual ${todayIso()}: ${passed}/${ran} passed`;
  const skipPart = skipped > 0 ? ` (${skipped} skipped, ${total} total)` : ` (${total} total)`;
  const text = freeText.trim();
  return text.length > 0 ? `${ranPart}${skipPart}. ${text}` : `${ranPart}${skipPart}.`;
}

function buildOverrideEntry(args: {
  capabilityId: string;
  status: RowStatus;
  confidence: number;
  note: string;
  expiresAt: string;
}): ManualOverride {
  // Output ISO with explicit time so the file diff is unambiguous.
  const expiryIso = `${args.expiresAt}T00:00:00.000Z`;
  return {
    capabilityId: args.capabilityId,
    status: args.status,
    confidence: Number(args.confidence.toFixed(4)),
    note: args.note,
    expiresAt: expiryIso,
  };
}

function mergeOverrides(file: OverridesFile, next: ManualOverride): OverridesFile {
  const others = (file.overrides ?? []).filter(
    (o) => o.capabilityId !== next.capabilityId,
  );
  return {
    version: file.version ?? 1,
    overrides: [...others, next].sort((a, b) =>
      a.capabilityId.localeCompare(b.capabilityId),
    ),
  };
}

function statusColor(status: RowStatus): string {
  switch (status) {
    case 'healthy':
      return 'text-green-700 dark:text-green-400';
    case 'watch':
      return 'text-yellow-700 dark:text-yellow-400';
    case 'broken':
      return 'text-red-700 dark:text-red-400';
    default:
      return 'text-muted-foreground';
  }
}

function CheckToggle({
  value,
  onChange,
  disabled,
}: {
  value: CheckState;
  onChange: (next: CheckState) => void;
  disabled?: boolean;
}) {
  const base =
    'inline-flex items-center justify-center px-2.5 py-1 text-xs font-medium border transition-colors';
  const variant: Record<CheckState, { active: string; inactive: string; Icon: typeof Check; label: string }> = {
    pass: {
      active: 'bg-green-600 text-white border-green-600',
      inactive: 'bg-background text-muted-foreground border-border hover:text-green-700 hover:border-green-300',
      Icon: Check,
      label: 'Pass',
    },
    fail: {
      active: 'bg-red-600 text-white border-red-600',
      inactive: 'bg-background text-muted-foreground border-border hover:text-red-700 hover:border-red-300',
      Icon: X,
      label: 'Fail',
    },
    skip: {
      active: 'bg-gray-500 text-white border-gray-500',
      inactive: 'bg-background text-muted-foreground border-border hover:text-foreground',
      Icon: Minus,
      label: 'Skip',
    },
  };

  return (
    <div className="inline-flex rounded-md overflow-hidden shadow-sm" role="radiogroup">
      {(['pass', 'fail', 'skip'] as const).map((opt, i) => {
        const v = variant[opt];
        const active = value === opt;
        const Icon = v.Icon;
        return (
          <button
            key={opt}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(opt)}
            className={`${base} ${active ? v.active : v.inactive} ${
              i === 0 ? 'rounded-l-md' : ''
            } ${i === 2 ? 'rounded-r-md' : ''} ${i === 1 ? '-ml-px' : ''} ${
              i === 2 ? '-ml-px' : ''
            }`}
          >
            <Icon className="h-3 w-3 mr-1" />
            {v.label}
          </button>
        );
      })}
    </div>
  );
}

export function ManualCheckDialog({
  open,
  onOpenChange,
  capability,
  currentOverridesFile,
  thresholds,
}: ManualCheckDialogProps) {
  const checks = capability.manualChecks ?? [];
  const [states, setStates] = useState<CheckState[]>(() => checks.map(() => 'skip'));
  const [note, setNote] = useState('');
  const [expiresAt, setExpiresAt] = useState(() =>
    isoDateInputDefault(DEFAULT_EXPIRY_DAYS),
  );

  // Reset form whenever the dialog opens so a previous capability's state
  // doesn't leak into a new run.
  useEffect(() => {
    if (open) {
      setStates(checks.map(() => 'skip'));
      setNote('');
      setExpiresAt(isoDateInputDefault(DEFAULT_EXPIRY_DAYS));
    }
    // checks length is static per capability — only re-run when open flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, capability.id]);

  const effectiveThresholds: Thresholds = useMemo(
    () => ({ ...DEFAULT_THRESHOLDS, ...(thresholds ?? {}) }),
    [thresholds],
  );

  const derived = useMemo(
    () => deriveManualStatus(states, effectiveThresholds),
    [states, effectiveThresholds],
  );

  const computedNote = useMemo(
    () => formatNote(derived.passed, derived.failed, derived.skipped, note),
    [derived, note],
  );

  const newOverride = useMemo<ManualOverride | null>(() => {
    if (derived.passed + derived.failed === 0) return null;
    return buildOverrideEntry({
      capabilityId: capability.id,
      status: derived.status,
      confidence: derived.confidence,
      note: computedNote,
      expiresAt,
    });
  }, [capability.id, derived, computedNote, expiresAt]);

  const newFile = useMemo<OverridesFile | null>(() => {
    if (!newOverride) return null;
    return mergeOverrides(currentOverridesFile, newOverride);
  }, [currentOverridesFile, newOverride]);

  const newFileJson = useMemo(
    () => (newFile ? JSON.stringify(newFile, null, 2) + '\n' : ''),
    [newFile],
  );

  const canSave = newOverride !== null;
  const existingOverride = (currentOverridesFile.overrides ?? []).find(
    (o) => o.capabilityId === capability.id,
  );

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(newFileJson);
      toast.success('Copied — paste into manual-overrides.json and refresh');
    } catch {
      toast.error('Clipboard write failed — copy from the box manually');
    }
  }

  function setOne(index: number, next: CheckState) {
    setStates((prev) => {
      const out = [...prev];
      out[index] = next;
      return out;
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            Run manual checks — {capability.label}
          </DialogTitle>
          <DialogDescription>
            Record this run's pass/fail tally for{' '}
            <code className="text-xs">{capability.id}</code>. The result is
            written into <code className="text-xs">manual-overrides.json</code>{' '}
            via clipboard — paste and refresh.
          </DialogDescription>
        </DialogHeader>

        {existingOverride && (
          <div className="text-xs text-muted-foreground border border-border rounded-md px-3 py-2 bg-muted/40">
            Replacing existing override — current{' '}
            <span className={statusColor(existingOverride.status)}>
              {existingOverride.status}
            </span>
            {typeof existingOverride.confidence === 'number' && (
              <> at {Math.round(existingOverride.confidence * 100)}%</>
            )}
            {existingOverride.expiresAt && (
              <> · expires {existingOverride.expiresAt.slice(0, 10)}</>
            )}
            .
          </div>
        )}

        {/* Checklist */}
        <div className="space-y-2">
          <Label>Checks ({checks.length})</Label>
          <div className="space-y-2">
            {checks.map((c, i) => (
              <div
                key={i}
                className="flex items-start justify-between gap-3 p-3 border border-border rounded-md"
              >
                <div className="text-sm flex-1 leading-snug">{c}</div>
                <div className="shrink-0">
                  <CheckToggle
                    value={states[i] ?? 'skip'}
                    onChange={(next) => setOne(i, next)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Note + expiry */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2 space-y-2">
            <Label htmlFor="manual-check-note">Observation (optional)</Label>
            <Textarea
              id="manual-check-note"
              placeholder="What did you see? Anything to dig into next time?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="manual-check-expires">Expires</Label>
            <Input
              id="manual-check-expires"
              type="date"
              value={expiresAt}
              min={todayIso()}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">
              After this date the override is dropped and the row reverts to its
              test-derived state.
            </p>
          </div>
        </div>

        {/* Live preview */}
        <div className="border border-border rounded-md p-3 bg-muted/30 space-y-1">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            Result
          </div>
          {canSave ? (
            <div className="text-sm">
              <span className="font-semibold uppercase tracking-wider text-xs mr-2">
                <span className={statusColor(derived.status)}>
                  {derived.status}
                </span>
              </span>
              <span className="text-muted-foreground">
                {derived.passed}/{derived.passed + derived.failed} ran ·{' '}
                {Math.round(derived.confidence * 100)}% confidence
                {derived.skipped > 0 && (
                  <> · {derived.skipped} skipped</>
                )}
              </span>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              Mark at least one check as Pass or Fail to compute a result. (All
              skips means nothing was decided — the row stays Unknown.)
            </div>
          )}
        </div>

        {/* JSON preview */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="manual-check-json">
              New <code>internal/control-grid/manual-overrides.json</code>
            </Label>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!canSave}
              onClick={copyToClipboard}
            >
              <Copy className="h-3.5 w-3.5 mr-1.5" />
              Copy to clipboard
            </Button>
          </div>
          <Textarea
            id="manual-check-json"
            readOnly
            value={
              newFileJson ||
              '// Mark at least one Pass or Fail to generate the override JSON.'
            }
            rows={10}
            className="font-mono text-xs"
            onFocus={(e) => e.currentTarget.select()}
          />
          <p className="text-[11px] text-muted-foreground">
            This is the full new file content. Open{' '}
            <code>internal/control-grid/manual-overrides.json</code>, replace
            its contents with what you copy here, save, and refresh — the row
            updates immediately.
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            disabled={!canSave}
            onClick={async () => {
              await copyToClipboard();
              onOpenChange(false);
            }}
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy &amp; close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
